/**
 * Energy wheel component creation logic
 */
import { DEFAULT_COEFFICIENTS, SectionCreationContext } from '../../../models/bot';
import { IBotSection, IErWheel } from '../../../models';
import { dispatchMutation } from '../../../utils/dispatch.utils';
import { erWheelApi } from '../../../services';

/**
 * Creates an energy wheel component for a section
 */
export async function createEnergyWheelComponent(
  botSection: IBotSection,
  sectionId: number,
  secondSectionId: number,
  context: SectionCreationContext,
  modelData: any,
): Promise<void> {
  const { getOptions, onProgress } = context;

  const input = modelData.input;
  const model = modelData.model;
  const energyWheelBody = {
    section_id: sectionId, // Current section in second tunnel
    section_2_id: secondSectionId, // Corresponding section in first tunnel
    energy_wheel_params: JSON.stringify({
      frost_control_winter: null,
      shipping_weight: model.shipping_weight,
      operating_weight: model.operating_weight,
      mfg_id: botSection.mfg_id,
      series_id: botSection.series_id,
      fin_pattern_id: 1,
      type_id: botSection.type_id,
      speed_control_id: input.speed_control_id,
      speed: input.speed,
      mtr_voltage_id: input.mtr_voltage_id,
      mounting_arr_id: 0,
      purge_angle_id: input.purge_angle_id,
      desiccant_id: 1,
      master_energy_wheel_id: model.id,
      rotor_style_id: botSection.rotor_style_id,
      rotor_depth_id: model.rotor_depth_id,
      rotor_depth: model.rotor_depth,
      wheel_size_id: model.wheel_size,
      wheel_size: model.wheel_size,
      seal_id: 1,
      drive_belt_id: 2,
      fin_thickness_id: 2,
      well_height_id: 3,
      hub_id: 1,
      wheel_height_id: null,
      casing_id: 1,
      casing_type_id: 1,
      purge_sector_location_id: input.purge_sector_location_id,
      temp_control_id: 1,
      rotation_detector_id: 1,
      coating_frame_id: 1,
      media_edge_id: 1,
      mtr_service_door_id: 1,
      bottom_plate_id: 1,
      coating_media_id: 1,
      configuration_id: 1,
      fin_pattern: 'Standard',
      min_face_velocity: input.min_face_velocity,
      max_face_velocity: input.max_face_velocity,
    }),
    ra_air_stream_winter_id: 4,
    ra_air_stream_summer_id: 4,
    oa_airflow_actual_summer: input.oa_airflow_actual_summer,
    oa_db_temp_summer: input.oa_db_temp_summer,
    oa_rh_summer: input.oa_rh_summer,
    oa_hr_summer: input.oa_hr_summer,
    oa_w_summer: input.oa_w_summer,

    sa_airflow_actual_summer: model.sa_airflow_actual_summer,
    sa_db_temp_summer: model.sa_db_temp_summer,
    sa_rh_summer: model.sa_rh_summer,
    sa_hr_summer: model.sa_hr_summer,
    sa_w_summer: model.sa_w_summer,
    sa_apd_summer: model.sa_apd_summer,

    ra_airflow_actual_summer: input.ra_airflow_actual_summer,
    ra_db_temp_summer: input.ra_db_temp_summer,
    ra_rh_summer: input.ra_rh_summer,
    ra_hr_summer: input.ra_hr_summer,
    ra_w_summer: input.ra_w_summer,

    ea_airflow_actual_summer: model.ea_airflow_actual_summer,
    ea_db_temp_summer: model.ea_db_temp_summer,
    ea_rh_summer: model.ea_rh_summer,
    ea_hr_summer: model.ea_hr_summer,
    ea_w_summer: model.ea_w_summer,
    ea_face_velocity_summer: Math.floor(model.ea_face_velocity_summer),
    sa_face_velocity_summer: Math.floor(model.sa_face_velocity_summer),

    ea_face_velocity_winter: Math.floor(model.ea_face_velocity_winter),
    sa_face_velocity_winter: Math.floor(model.sa_face_velocity_winter),

    ea_apd_summer: model.ea_apd_summer,

    sensible_efficiency_summer: model.sensible_efficiency_summer,
    sensible_capacity_summer: Math.floor(model.sensible_capacity_summer),
    latent_efficiency_summer: model.latent_efficiency_summer,
    latent_capacity_summer: Math.floor(model.latent_capacity_summer),
    total_efficiency_summer: model.total_efficiency_summer,
    total_capacity_summer: Math.floor(model.total_capacity_summer),

    oa_airflow_actual_winter: input.oa_airflow_actual_winter,
    oa_airflow_std_winter: input.oa_airflow_std_winter,
    min_face_velocity: input.min_face_velocity,
    max_face_velocity: input.max_face_velocity,
    oa_rh_winter: input.oa_rh_winter,
    oa_hr_winter: input.oa_hr_winter,
    oa_w_winter: input.oa_w_winter,

    sa_airflow_actual_winter: model.sa_airflow_actual_winter,
    sa_db_temp_winter: model.sa_db_temp_winter,
    sa_rh_winter: model.sa_rh_winter,
    sa_hr_winter: model.sa_hr_winter,
    sa_w_winter: model.sa_w_winter,
    sa_apd_winter: model.sa_apd_winter,

    ra_airflow_actual_winter: input.ra_airflow_actual_winter,
    ra_db_temp_winter: input.ra_db_temp_winter,
    ra_rh_winter: input.ra_rh_winter,
    ra_hr_winter: input.ra_hr_winter,
    ra_w_winter: input.ra_w_winter,

    ea_airflow_actual_winter: model.ea_airflow_actual_winter,
    ea_db_temp_winter: model.ea_db_temp_winter,
    ea_rh_winter: model.ea_rh_winter,
    ea_hr_winter: model.ea_hr_winter,
    ea_w_winter: model.ea_w_winter,
    ea_apd_winter: model.ea_apd_winter,

    unit_effectiveness_winter: model.unit_effectiveness_winter,

    sensible_efficiency_winter: model.sensible_efficiency_winter,
    sensible_capacity_winter: Math.floor(model.sensible_capacity_winter),
    latent_efficiency_winter: model.latent_efficiency_winter,
    latent_capacity_winter: Math.floor(model.latent_capacity_winter),
    total_efficiency_winter: model.total_efficiency_winter,
    total_capacity_winter: Math.floor(model.total_capacity_winter),

    condensate_winter: model.condensate_winter,
    eatr_winter: model.eatr_winter,
    oacf_winter: model.oacf_winter,
    eatr_summer: model.eatr_summer,
    oacf_summer: model.oacf_summer,

    fan_locations_id: input.fan_locations_id,
    speed_control_id: input.speed_control_id,
    speed: input.speed,
    rated_datetime: input.rated_datetime,
    rating_warnings: model.rating_warnings,
    rating_notes: model.rating_notes,
    master_energy_wheel_id: model.id,
    shipping_weight: model.shipping_weight,

    difference_in_pressure_winter: input.difference_in_pressure_winter,
    difference_in_pressure_summer: input.difference_in_pressure_summer,

    drive_belt_id: model.drive_belt_id,
    certification_id: 2,

    oa_sp: input.oa_sp,
    ra_sp: input.ra_sp,
    oa_db_temp_winter: input.oa_db_temp_winter,

    oa_wb_temp_summer: input.oa_wb_temp_summer,
    oa_wb_temp_winter: input.oa_wb_temp_winter,
    ra_wb_temp_summer: input.ra_wb_temp_summer,
    ra_wb_temp_winter: input.ra_wb_temp_winter,
  };

  // Create energy wheel component
  const e_w: IErWheel = await dispatchMutation(
    erWheelApi.endpoints.createEnergyWheel,
    {
      body: energyWheelBody,
    },
    getOptions(),
  );

  await dispatchMutation(
    erWheelApi.endpoints.updateEnergyWheel,
    {
      id: e_w?.id,
      body: {
        ra_air_stream_winter_id: 4,
        ra_air_stream_summer_id: 4,
      },
    },
    getOptions(),
  );

  onProgress?.(DEFAULT_COEFFICIENTS.er_wheel_create);
}
